<template>
  <v-card data-testid="regimen_exercise_edit">
    <v-card-text>
      <v-layout>
        <v-flex md4>
          <ExercisePresetSelect
            :label="$t('common.presets')"
            :current_exercise_config="localValue.config"
            :instructions="localValue.instructions"
            :exercise_id="localValue.exercise_id"
            :preset_id="preset ? preset.id : null"
            @preset_selected="preset_selected"
          />
        </v-flex>

        <v-flex xs5 offset-xs3>
          <v-select v-model="localValue.customization" outlined :label="$t('common.customization')" :items="customization_options" />
        </v-flex>
      </v-layout>

      <v-layout>
        <v-flex xs4>
          <v-text-field
            v-model="localValue.title"
            outlined
            :label="$t('common.title')"
            :hint="$t('regimen_exercise_edit.title_hint')"
            @input="title_input"
          />
        </v-flex>

        <v-flex xs7 offset-xs1>
          <v-textarea
            v-if="localValue.exercise_id"
            v-model="localValue.instructions"
            outlined
            auto-grow
            rows="2"
            :label="$t('common.special_instructions')"
            :hint="$t('common.special_instructions_for_exercise')"
            @change="$emit('change', value)"
          />
        </v-flex>
      </v-layout>

      <v-layout v-if="app_config.exercise_education">
        <v-flex xs12>
          <QuillEditor v-model="localValue.exercise_education" :placeholder="$t('common.education')" style="height: 100px; margin-bottom: 40px" />
        </v-flex>
      </v-layout>

      <v-divider v-if="show_level_controls" class="mb-8" />

      <v-layout v-if="show_level_controls" style="margin-bottom: -15px">
        <v-flex md3>
          <v-select
            v-model="localValue.level"
            outlined
            dense
            :items="level_select"
            :hint="$t('regimen_exercise_edit.user_will_start_at_this_level')"
            :label="$t('common.start_level')"
            persistent-hint
            @change="level_change"
          />
        </v-flex>

        <v-flex md3 offset-md1>
          <v-select
            v-model="localValue.max_level"
            outlined
            dense
            :items="max_level_select"
            :hint="$t('regimen_exercise_edit.max_level_hint')"
            :label="$t('regimen_exercise_edit.max_level')"
            persistent-hint
          />
        </v-flex>

        <v-flex md3 offset-md1 style="margin-top: -20px">
          <v-checkbox
            v-model="localValue.automatic_levelling"
            :label="$t('regimen_exercise_edit.automatic_levelling')"
            :hint="automatic_levelling_hint"
            persistent-hint
          />
        </v-flex>
      </v-layout>

      <v-alert v-if="is_level_deprecated_partial" style="margin-top: 25px; margin-bottom: -10px; font-size: small" outlined dense type="warning">{{ $t("regimen_exercise_edit.level_deprecated") }}</v-alert>

      <v-divider style="margin-top: 40px; margin-bottom: 20px" />

      <div v-if="localValue.exercise_id" style="margin-left: -10px">
        <ExerciseConfigForm
          ref="exerciseConfig"
          v-model="localValue.config"
          :exercise_id="localValue.exercise_id"
          context="regimen_edit"
          @change="$emit('change', value)"
        />
      </div>
    </v-card-text>

    <v-alert v-for="error in errors" :key="error" :value="true" type="error" data-testid="regimen_exercise_edit_error">{{ error }}</v-alert>

    <v-card-actions>
      <v-spacer />
      <v-btn v-if="localValue.exercise_id" color="primary" @click="save" data-testid="regimen_exercise_edit_save">{{ $t("common.save") }}</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import OpticalGymConfigForms from "../../../exercises/OpticalGymConfigForms.js";
import { getExerciseDefaultConfig } from "../classes/exercise";
import ExerciseConfigForm from "./ExerciseConfigForm.vue";
import { mapState } from "vuex";
import Vue from "vue";
import QuillEditor from "./QuillEditor";

export default {
  components: Object.assign(OpticalGymConfigForms, {
    QuillEditor,
    ExerciseConfigForm,
  }),
  props: {
    value: {
      type: Object,
      required: true,
    },
    allow_select_exercise: {
      type: Boolean,
      default: () => true,
    },
  },
  data() {
    let data = {
      errors: [],
      preset: null,
      custom_title_locked: false,
      skip_title_lock_update: false,
      customization_options: [
        {
          text: this.$t("regimen_exercise_edit.dont_allow_customization_no_levelling"),
          value: "no_customization_no_levelling",
        },
        {
          text: this.$t("regimen_exercise_edit.dont_allow_customization"),
          value: "no_customization",
        },
        {
          text: this.$t("regimen_exercise_edit.allow_basic_customization"),
          value: "basic",
        },
        {
          text: this.$t("regimen_exercise_edit.allow_advanced_customization"),
          value: "advanced",
        },
      ],

    };
    return data;
  },
  computed: {
    ...mapState(["exercises", "app_config"]),
    localValue: {
      get() {
        let value = this.value;
        let exercise = null;
        let level_deprecation_mode = null;

        if (value.exercise_id) {
          exercise = this.exercises[value.exercise_id];
          if (exercise) {
            if (exercise.level_deprecated === "partial") {
              level_deprecation_mode = "partial";
            } else if (exercise.level_deprecated === "full") {
              level_deprecation_mode = "full";
            }
          }
        }

        if (value.exercise_id && Object.entries(value.config).length === 0) {
          value.config = getExerciseDefaultConfig(exercise, window.OpticalGymApp);
        }

        // Full level deprecation disables levelling entirely.
        if (level_deprecation_mode === "full") {
          value.automatic_levelling = false;
          if (value.customization !== "no_customization_no_levelling") {
            value.customization = "no_customization_no_levelling";
          }
        } else if (value.automatic_levelling === undefined) {
          // Automatic levelling should be ON by default.
          value.automatic_levelling = level_deprecation_mode !== "partial";
        }

        // customization should be set to "no_customization" by default
        if (!value.customization) {
          if (level_deprecation_mode === "partial") {
            value.customization = "no_customization_no_levelling";
          } else {
            value.customization = "no_customization";
          }
        }

        // title should be set to the exercise title by default
        if (!value.title) {
          if (this.exercises[value.exercise_id].title) {
            let locale = this.$i18n.lang() || "en";
            value.title = this.exercises[value.exercise_id].title[locale];
          }
        }

        if (!exercise) {
          return value;
        }
        let exercise_config_form = OpticalGymConfigForms[value.exercise_id];
        let current_config_version = value.config_version || 0;

        // Upgrade the config if needed
        if (
          current_config_version < exercise.config_version &&
          exercise_config_form &&
          exercise_config_form.methods &&
          exercise_config_form.methods.upgrade_config
        ) {
          exercise_config_form.methods.upgrade_config(value.config, current_config_version, exercise.config_version);
        }
        value.config_version = exercise.config_version;

        // Preprocess the config if OpticalGymConfigForms has a config preprocessor
        if (exercise_config_form && exercise_config_form.methods && exercise_config_form.methods.preprocess_config) {
          OpticalGymConfigForms[value.exercise_id].methods.preprocess_config(value.config);
        }

        return value;
      },
      set(localValue) {
        this.$emit("input", localValue);
      },
    },
    has_levels: function () {
      if (this.localValue.exercise_id) {
        return this.exercises[this.localValue.exercise_id].num_levels > 1;
      }
      return false;
    },
    show_level_controls() {
      return this.has_levels && !this.is_level_deprecated_full;
    },
    is_level_deprecated_partial() {
      return this.level_deprecation_mode === "partial";
    },
    is_level_deprecated_full() {
      return this.level_deprecation_mode === "full";
    },
    level_deprecation_mode() {
      if (!this.current_exercise) {
        return null;
      }

      if (this.current_exercise.level_deprecated === "partial") {
        return "partial";
      }
      if (this.current_exercise.level_deprecated === "full") {
        return "full";
      }
      return null;
    },
    level_select: function () {
      let levels = [];
      if (this.localValue.exercise_id) {
        let exercise = this.exercises[this.localValue.exercise_id];
        for (let i = 1; i <= exercise.num_levels; i++) {
          levels.push({ value: i, text: this.$t("common.level_list", { level_num: i })});
        }
      }
      return levels;
    },
    max_level_select: function () {
      let levels = [];
      if (this.localValue.exercise_id) {
        let exercise = this.exercises[this.localValue.exercise_id];
        for (let i = this.localValue.level; i <= exercise.num_levels; i++) {
          levels.push({ value: i, text: this.$t("common.level_list", { level_num: i })});
        }
      }
      levels.reverse();
      return levels;
    },
    automatic_levelling_hint() {
      return this.$t("regimen_exercise_edit.if_checked_user_progress");

      // TODO: fix reactivity
      //if (this.localValue.automatic_levelling) {
      //  return "The user will automatically progress to the next level when they reach the requisite score.";
      //} else {
      //  return "You will need to manually change the user's level.";
      //}
    },
    current_exercise() {
      if (!this.localValue.exercise_id) {
        return null;
      }
      return this.exercises[this.localValue.exercise_id];
    },
  },
  watch: {
    "localValue.config.variant"(new_variant, previous_variant) {
      if (new_variant === previous_variant) {
        return;
      }

      if (this.preset) {
        return;
      }

      this.update_auto_title({
        previous_preset: null,
        next_preset: null,
        previous_variant,
        next_variant: new_variant,
      });
    },
  },
  created() {
    this.initialize_custom_title_lock();
  },
  methods: {
    title_input() {
      if (this.skip_title_lock_update || this.custom_title_locked) {
        return;
      }

      if (!this.localValue.title) {
        return;
      }

      let auto_title = this.auto_title_for({
        preset: this.preset,
        variant: this.localValue.config ? this.localValue.config.variant : undefined,
      });
      if (this.localValue.title.trim() !== auto_title) {
        this.custom_title_locked = true;
      }
    },
    initialize_custom_title_lock() {
      if (!this.localValue || !this.localValue.title) {
        return;
      }

      let auto_title = this.auto_title_for({
        preset: this.preset,
        variant: this.localValue.config ? this.localValue.config.variant : undefined,
      });
      this.custom_title_locked = this.localValue.title.trim() !== auto_title;
    },
    get_exercise_title() {
      if (!this.current_exercise || !this.current_exercise.title) {
        return "";
      }
      let locale = this.$i18n.lang() || "en";
      return this.current_exercise.title[locale] || this.current_exercise.title.en || "";
    },
    get_variant_title(variant_key) {
      if (!variant_key || !this.current_exercise || !this.current_exercise.variants) {
        return null;
      }

      let variant = this.current_exercise.variants[variant_key];
      if (!variant || !variant.title) {
        return null;
      }

      let locale = this.$i18n.lang() || "en";
      return variant.title[locale] || variant.title.en || null;
    },
    should_copy_variant_to_title(variant_key) {
      if (!variant_key || !this.current_exercise || !this.current_exercise.variants) {
        return false;
      }

      let variant = this.current_exercise.variants[variant_key];
      return !!(variant && variant.copy_to_title);
    },
    auto_title_for({ preset, variant }) {
      let exercise_title = this.get_exercise_title();

      if (preset) {
        return exercise_title + " - " + preset.title;
      }

      if (this.should_copy_variant_to_title(variant)) {
        let variant_title = this.get_variant_title(variant);
        if (variant_title) {
          return exercise_title + " - " + variant_title;
        }
      }

      return exercise_title;
    },
    update_auto_title({ previous_preset, next_preset, previous_variant, next_variant }) {
      if (this.custom_title_locked) {
        return;
      }

      let previous_title = this.auto_title_for({
        preset: previous_preset,
        variant: previous_variant,
      });
      let next_title = this.auto_title_for({
        preset: next_preset,
        variant: next_variant,
      });

      if (!this.localValue.title || this.localValue.title.trim() === previous_title) {
        this.skip_title_lock_update = true;
        this.localValue.title = next_title;
        this.$nextTick(() => {
          this.skip_title_lock_update = false;
        });
      }
    },
    preset_selected(preset) {
      let previous_preset = this.preset;
      let previous_variant = this.localValue.config ? this.localValue.config.variant : undefined;
      if (preset) {
        Object.assign(this.localValue.config, preset.config);
        this.localValue.instructions = preset.instructions;
      } else {
        // NULL preset, meaning default config
        let assign_config = getExerciseDefaultConfig(this.current_exercise, window.OpticalGymApp);

        // Delete level and texturl
        // TODO: texturl et al should be defined in the exercise config params yml
        if ("level" in assign_config) {
          delete assign_config.level;
        }
        if ("texturl" in assign_config) {
          delete assign_config.texturl;
        }

        Object.assign(this.localValue.config, assign_config);
        this.localValue.instructions = "";

        // Apply local level config
        if (this.current_exercise.num_levels && this.localValue.config.level) {
          Object.assign(this.localValue.config, this.current_exercise["level" + this.config.level]);
        }
      }

      this.preset = preset;
      let next_variant = this.localValue.config ? this.localValue.config.variant : undefined;
      this.update_auto_title({
        previous_preset,
        next_preset: preset,
        previous_variant,
        next_variant,
      });
      this.$forceUpdate();
      this.$emit("change", this.localValue);
    },
    level_change(new_level) {
      // Override config with level config
      let exercise = this.exercises[this.localValue.exercise_id];
      let level_config = exercise["level" + new_level];
      for (var param in level_config) {
        Vue.set(this.localValue.config, param, level_config[param]);
      }

      // If the max-level is less than the current level, set it to the current level
      if (this.localValue.max_level <= new_level) {
        this.localValue.max_level = new_level;
      }

      this.$forceUpdate();
      this.$emit("change", this.localValue);
    },

    // Enforce min-max and allowed values
    enforce_allowed_values() {
      let exercise = this.exercises[this.localValue.exercise_id];
      let default_config = getExerciseDefaultConfig(exercise, window.OpticalGymApp);
      if (exercise.params) {
        for (const [key, param_config] of Object.entries(exercise.params)) {
          if (this.localValue.config[key]) {
            if (param_config.min !== null && this.localValue.config[key] < param_config.min) {
              this.localValue.config[key] = param_config.min;
            }
            if (param_config.max !== null && this.localValue.config[key] > param_config.max) {
              this.localValue.config[key] = param_config.max;
            }
            if (param_config.allowed !== null && !param_config.allowed.includes(this.localValue.config[key])) {
              this.localValue.config[key] = default_config[key];
              if (
                exercise.num_levels &&
                this.localValue.config.level &&
                this.exercise["level" + this.localValue.config.level] &&
                this.exercise["level" + this.localValue.config.level][key] !== undefined
              ) {
                this.localValue.config[key] = exercise["level" + this.localValue.config.level][key];
              }
            }
          }
        }
      }
    },

    save() {
      // Enforce allowed values and min / max
      this.enforce_allowed_values();

      // Apply default units (prism/arcsec) to vergence/depth fields that have no unit
      let exercise = this.$refs.exerciseConfig;
      if (exercise && typeof exercise.applyDefaultUnits === "function") {
        exercise.applyDefaultUnits();
      }

      // Check for errors
      this.errors = [];
      if (typeof exercise.validate == "function") {
        this.errors = exercise.validate(this.value.config, "regimen_edit");
      }

      if (this.errors.length) {
        return;
      }

      this.$emit("save");
    },
  },
};
</script>

<style>
/* RTL languages */
html[lang="he"] .ql-editor,
html[lang="ar"] .ql-editor {
  text-align: right;
}
</style>
